import type { RequestResult } from '@trellis/shared'

import { env } from '@/env'

export interface InvitationPreviewResponse {
  email: string
  tenantDisplayName: string
  hasExistingAccount: boolean
}

export async function getInvitationPreview(
  token: string,
): Promise<RequestResult<InvitationPreviewResponse>> {
  try {
    const response = await fetch(`${env.VITE_API_BASE_URL}/api/invitations/${token}`, {
      credentials: 'include',
    })

    const body = await response.json()

    if (!response.ok) {
      return {
        status: response.status,
        data: null,
        pagination: null,
        error: {
          code: body.code ?? 'API_ERROR',
          message: body.message ?? 'Failed to load invitation',
        },
      }
    }

    return {
      status: response.status,
      data: body.data as InvitationPreviewResponse,
      pagination: null,
      error: null,
    }
  } catch (caughtError) {
    return {
      status: 500,
      data: null,
      pagination: null,
      error: {
        code: 'NETWORK_ERROR',
        message: caughtError instanceof Error ? caughtError.message : 'Request failed',
      },
    }
  }
}

export interface AcceptInvitationRequest {
  name?: string
  password: string
}

export async function acceptInvitation(
  token: string,
  body: AcceptInvitationRequest,
): Promise<RequestResult<{ success: boolean }>> {
  try {
    const response = await fetch(
      `${env.VITE_API_BASE_URL}/api/invitations/${token}/accept`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    )

    const responseBody = await response.json()

    if (!response.ok) {
      return {
        status: response.status,
        data: null,
        pagination: null,
        error: {
          code: responseBody.code ?? 'API_ERROR',
          message: responseBody.message ?? 'Failed to accept invitation',
        },
      }
    }

    return {
      status: response.status,
      data: responseBody.data as { success: boolean },
      pagination: null,
      error: null,
    }
  } catch (caughtError) {
    return {
      status: 500,
      data: null,
      pagination: null,
      error: {
        code: 'NETWORK_ERROR',
        message: caughtError instanceof Error ? caughtError.message : 'Request failed',
      },
    }
  }
}
